import { ArrowRight } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { describeApiError } from "../../lib/api";
import { AuthLayout, EmailField, FormError, PasswordField } from "../AuthPages";
import { PARTNER_BASE } from "./PartnerShell";
import { ButtonLabel } from "../../components/ButtonLabel";

/**
 * Sign-in for the lending partner's reviewers. Same credentials backend as the customer app; what
 * differs is where a session lands and that there is no self-service registration: reviewer
 * accounts are created by an Aventuria administrator.
 */
export function PartnerLoginPage() {
  const { user, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string[] | null>(null);

  if (user && (user.role === "lender" || user.role === "admin")) return <Navigate to={PARTNER_BASE} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true); setError(null);
    try {
      await login(email, password);
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos iniciar sesión."));
    } finally { setSubmitting(false); }
  };

  return (
    <AuthLayout kicker="Portal de aliados · Peso a Peso" headline="Revisa las solicitudes de crédito de Aventuria." promise="Aquí encuentras cada solicitud de «Viaja ahora, paga después» con sus datos, su constancia fiscal y la autorización del solicitante.">
      {user ? (
        <div className="auth-form">
          <h2 className="partner-login-title">Esta cuenta no tiene acceso al portal</h2>
          <p className="field-hint">Iniciaste sesión como <strong>{user.email}</strong>, una cuenta de cliente de Aventuria. El portal de aliados es solo para el equipo de Peso a Peso.</p>
          <Link className="primary-button full" to="/inicio">Ir a mi cuenta Aventuria</Link>
          <button type="button" className="secondary-button full" onClick={() => void logout()}>Cerrar sesión y entrar con otra cuenta</button>
        </div>
      ) : (
        <form className="auth-form" onSubmit={submit}>
          <h2 className="partner-login-title">Acceso para revisores</h2>
          <EmailField value={email} onChange={setEmail} />
          <PasswordField label="Contraseña" value={password} onChange={setPassword} link={<Link to="/recuperar">¿La olvidaste?</Link>} />
          <FormError messages={error} />
          <button className="primary-button full" type="submit" disabled={submitting}>
            <ButtonLabel busy={submitting} busyLabel="Entrando" label="Entrar al portal" icon={<ArrowRight size={16} aria-hidden="true" />} />
          </button>
          <p className="auth-footnote">¿Sin acceso todavía? Pide a Aventuria que dé de alta tu cuenta de revisor.</p>
        </form>
      )}
    </AuthLayout>
  );
}
